import { supabase } from './supabase';
import { enqueueAiJob, type AiJob } from './aiJobs';
import { formatAttachmentLines, uploadJobAttachments } from './aiJobAttachments';
import type { ContentBrief, ContentBriefAttachment } from './supabase.types';
import { PROMPT_DEFAULTS, loadPromptTemplate, renderPrompt } from './prompts';

export type { ContentBrief, ContentBriefAttachment };

export type EnqueueContentInput = {
  title: string;
  primary: string;
  secondary: string[];
  clientId: string;
  clientName: string;
  repoId: string | null;
  repoName: string | null;
  repoUrl: string | null;
  files: File[];
  createdBy: { id: string; name: string };
  /** One of AI_JOB_MODELS ids. Omit / null = agent default. */
  model?: string | null;
};

function noonTodayIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12).toISOString();
}

/** The "send-to-writer" prompt (Agents → Prompts): the brief the content-article skill works from. */
export function formatContentQueueNote(brief: ContentBrief, template: string = PROMPT_DEFAULTS['send-to-writer']): string {
  return renderPrompt(template, {
    primary: brief.primary,
    secondary: brief.secondary.join(', '),
    repo: brief.repo_url ? `${brief.repo_name || brief.repo_url} (${brief.repo_url})` : 'none — article lands in Content → Pieces (cockpit content put)',
    attachments: brief.attachments.length > 0 ? ['Attachments:', ...formatAttachmentLines(brief.attachments)].join('\n') : '',
  });
}

export async function enqueueContentJob(input: EnqueueContentInput): Promise<AiJob> {
  const title = input.title.trim();
  const primary = input.primary.trim();
  if (!title) throw new Error('Title is required');
  if (!primary) throw new Error('Primary search phrase is required');
  if (!input.clientId) throw new Error('Pick a client so the writer can load Voice');
  const template = await loadPromptTemplate('send-to-writer');

  const secondary = input.secondary.map((s) => s.trim()).filter(Boolean);
  const description = [
    'Content brief — agent queue.',
    '',
    `Primary search phrase: ${primary}`,
    secondary.length > 0 ? `Secondary: ${secondary.join(', ')}` : null,
    input.repoUrl ? `Repo: ${input.repoName || input.repoUrl} (${input.repoUrl})` : 'Repo: none',
  ]
    .filter(Boolean)
    .join('\n');

  const { data: task, error: taskError } = await supabase
    .from('client_tasks')
    .insert({
      client_id: input.clientId,
      title,
      description,
      status: 'In Progress',
      due_date: noonTodayIso(),
      assigned_by: input.createdBy.id,
      assigned_to: input.createdBy.id,
      assigned_to_name: input.createdBy.name,
    })
    .select('id')
    .single();
  if (taskError) throw taskError;

  const brief: ContentBrief = {
    title,
    primary,
    secondary,
    client_id: input.clientId,
    client_name: input.clientName,
    repo_id: input.repoId,
    repo_name: input.repoName,
    repo_url: input.repoUrl,
    attachments: [],
  };

  const job = await enqueueAiJob({
    task_id: task.id,
    title,
    card_id: null,
    created_by: input.createdBy.id,
    queue_note: formatContentQueueNote(brief, template),
    kind: 'content',
    brief,
    model: input.model ?? null,
  });

  if (input.files.length === 0) return job;

  const attachments = await uploadJobAttachments(job.id, input.files);
  const nextBrief = { ...brief, attachments };
  const { data: updated, error: patchError } = await supabase
    .from('ai_jobs')
    .update({
      brief: nextBrief,
      queue_note: formatContentQueueNote(nextBrief, template),
    })
    .eq('id', job.id)
    .select('*')
    .single();
  if (patchError) throw patchError;
  return updated as AiJob;
}
